/**
 * Shift Scheduling Activity Log Types
 * Types for tracking manager/employee scheduling actions on shifts
 */

export type ShiftSchedulingEventType =
  | 'shift_created'
  | 'shift_edited'
  | 'shift_moved'
  | 'shift_deleted'
  | 'employee_assigned'
  | 'employee_unassigned'
  | 'employee_self_requested'
  | 'employee_request_approved'
  | 'employee_request_rejected';

export type ActorType = 'manager' | 'employee' | 'system';

export interface ShiftSchedulingActivityMetadata {
  // For shift_created
  source?: 'calendar' | 'template' | 'drag-drop' | 'copy' | 'quick-create';

  // For shift_edited
  changes?: {
    title?: { old: string | null; new: string | null };
    notes?: { old: string | null; new: string | null };
    breaks?: { old: unknown; new: unknown };
  };

  // For shift_moved
  previousStart?: string;
  previousEnd?: string;
  newStart?: string;
  newEnd?: string;
  previousDate?: string;
  newDate?: string;

  // For employee assignments
  previousEmployeeId?: string | null;
  previousEmployeeName?: string | null;
  newEmployeeName?: string | null;

  // For shift requests
  requestId?: string;
  employeeNotes?: string;
  managerNotes?: string;
}

export interface ShiftSchedulingActivityEvent {
  id: string;
  business_id: string;
  shift_id: string | null;
  event_type: ShiftSchedulingEventType;
  event_timestamp: string;
  created_at: string;

  actor_user_id: string | null;
  actor_employee_id: string | null;
  actor_type: ActorType;

  target_employee_id: string | null;

  shift_title: string | null;
  shift_date: string;
  shift_start_time: string | null;
  shift_end_time: string | null;

  metadata: ShiftSchedulingActivityMetadata;
  description: string | null;
}

// Extended type with joined employee data
export interface ShiftSchedulingActivityEventWithDetails extends ShiftSchedulingActivityEvent {
  actor_employee?: {
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null;
  target_employee?: {
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null;
}

export interface ShiftSchedulingActivityFilters {
  dateRange?: { start: Date; end: Date };
  eventTypes?: ShiftSchedulingEventType[];
  actorTypes?: ActorType[];
  employeeIds?: string[];
}

// Event display configuration
export interface SchedulingActivityEventConfig {
  icon: string;
  color: string;
  bgColor: string;
  dotColor: string; // Solid color for small dot indicators
  label: string;
}

export const SCHEDULING_EVENT_CONFIG: Record<ShiftSchedulingEventType, SchedulingActivityEventConfig> = {
  shift_created: {
    icon: 'Plus',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    dotColor: 'bg-green-500',
    label: 'Shift Created',
  },
  shift_edited: {
    icon: 'Pencil',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    dotColor: 'bg-blue-500',
    label: 'Shift Edited',
  },
  shift_moved: {
    icon: 'Move',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    dotColor: 'bg-purple-500',
    label: 'Shift Moved',
  },
  shift_deleted: {
    icon: 'Trash2',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    dotColor: 'bg-red-500',
    label: 'Shift Deleted',
  },
  employee_assigned: {
    icon: 'UserPlus',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    dotColor: 'bg-indigo-500',
    label: 'Employee Assigned',
  },
  employee_unassigned: {
    icon: 'UserMinus',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    dotColor: 'bg-orange-500',
    label: 'Employee Unassigned',
  },
  employee_self_requested: {
    icon: 'Hand',
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    dotColor: 'bg-cyan-500',
    label: 'Shift Requested',
  },
  employee_request_approved: {
    icon: 'CheckCircle',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    dotColor: 'bg-emerald-500',
    label: 'Request Approved',
  },
  employee_request_rejected: {
    icon: 'XCircle',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    dotColor: 'bg-rose-500',
    label: 'Request Rejected',
  },
};
